import React from "react";

export const memberDisplayName = (member) => {
  const pi = member?.personal_info || member || {};
  return `${pi.first_name || ""} ${pi.last_name || ""}`.trim();
};

const findMember = (members, id) =>
  (members || []).find((m) => String(m.id) === String(id));

const selectCls =
  "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white";
const lblCls = "block text-xs font-medium text-gray-500 mb-1";

const MemberOptionList = ({ members, excludeIds, currentId }) => (
  <>
    <option value="">Select member...</option>
    {members
      .filter(
        (m) =>
          String(m.id) === String(currentId) ||
          !excludeIds.map(String).includes(String(m.id)),
      )
      .map((m) => (
        <option key={m.id} value={m.id}>
          {memberDisplayName(m) || m.id}
        </option>
      ))}
  </>
);

const MemberSelect = ({
  members = [],
  multi = false,
  value,
  onChange,
  excludeIds = [],
  isEditing = true,
  label,
  required = false,
  error,
  placeholder = "Select member...",
  addLabel = "+ Add",
  maxEntries,
}) => {
  const entries = multi
    ? (Array.isArray(value) ? value : []).map((v) => (v && v.id) || "")
    : [];

  if (!isEditing) {
    const names = multi
      ? entries.map((id) => memberDisplayName(findMember(members, id))).filter(Boolean)
      : [memberDisplayName(findMember(members, value))].filter(Boolean);
    return (
      <div>
        {label && (
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {label}
          </label>
        )}
        <p className="text-sm text-gray-800 py-1">
          {names.length ? names.join(", ") : <span className="text-gray-400 italic">—</span>}
        </p>
      </div>
    );
  }

  if (!multi) {
    return (
      <div>
        {label && (
          <label className={lblCls}>
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        )}
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={selectCls}
        >
          <MemberOptionList members={members} excludeIds={excludeIds} currentId={value} />
        </select>
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>
    );
  }

  const setEntry = (idx, id) => {
    const next = entries.map((e, i) => (i === idx ? id : e));
    onChange(next.map((id2) => ({ id: id2 })));
  };
  const removeEntry = (idx) => {
    onChange(entries.filter((_, i) => i !== idx).map((id2) => ({ id: id2 })));
  };
  const addEntry = () => {
    onChange([...entries, ""].map((id2) => ({ id: id2 })));
  };

  return (
    <div>
      {label && (
        <label className={lblCls}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="space-y-2">
        {entries.map((id, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <select
              value={id || ""}
              onChange={(e) => setEntry(idx, e.target.value)}
              className={selectCls}
            >
              <MemberOptionList
                members={members}
                excludeIds={[...excludeIds, ...entries.filter((_, i) => i !== idx)]}
                currentId={id}
              />
            </select>
            <button
              type="button"
              onClick={() => removeEntry(idx)}
              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded hover:bg-red-50"
              title="Remove"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        ))}
        {(!maxEntries || entries.length < maxEntries) && (
          <button
            type="button"
            onClick={addEntry}
            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            {addLabel}
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default MemberSelect;
